import http from "./http";
import { API_BASE_URL } from "./apiConfig";

class FileService {
  // ============= FILE UPLOAD =============

  async uploadFile(file, entityType, entityId, assetType, isPublic = false) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("entity_type", entityType);
    formData.append("entity_id", entityId);
    formData.append("asset_type", assetType);
    formData.append("is_public", isPublic.toString());

    const res = await http.post("/files/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data;
  }

  // ============= FILE ACCESS =============

  getFileUrl(assetId, download = false) {
    return `${API_BASE_URL}/files/${assetId}${download ? "?download=true" : ""}`;
  }

  getPublicFileUrl(assetId) {
    return `${API_BASE_URL}/files/public/${assetId}`;
  }

  async getEntityAssets(entityType, entityId, assetType = null) {
    const params = assetType ? { asset_type: assetType } : {};
    const res = await http.get(`/files/entity/${entityType}/${entityId}`, { params });
    return res.data;
  }

  async deleteFile(assetId) {
    const res = await http.delete(`/files/${assetId}`);
    return res.data;
  }

  // ============= NARRATOR DEMOS =============

  async uploadNarratorDemo(file, title = null, description = null, tags = null) {
    const formData = new FormData();
    formData.append("file", file);
    if (title) formData.append("title", title);
    if (description) formData.append("description", description);
    if (tags) formData.append("tags", JSON.stringify(tags));

    const res = await http.post("/narrator/demos/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data;
  }

  async getNarratorDemos(narratorId) {
    const res = await http.get(`/narrators/${narratorId}/demos`);
    return res.data;
  }

  // ============= PROJECT CHAPTER AUDIO =============

  async uploadChapterAudio(projectId, file, chapterLabel) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("chapter_label", chapterLabel);

    const res = await http.post(`/narrator/projects/${projectId}/chapters/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data;
  }

  async getProjectAudio(projectId) {
    const res = await http.get(`/narrator/projects/${projectId}/audio`);
    return res.data;
  }

  // ============= BOOK ASSETS =============

  async getBookAssets(bookId, assetType = null) {
    const params = assetType ? { asset_type: assetType } : {};
    const res = await http.get(`/books/${bookId}/assets`, { params });
    return res.data;
  }

  // ============= VALIDATION =============

  validateDocumentFile(file) {
    const allowedTypes = [".pdf", ".epub", ".mobi"];
    const maxSize = 200 * 1024 * 1024; // 200MB

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!allowedTypes.includes(`.${ext}`)) {
      return { valid: false, error: `Invalid file type. Allowed: ${allowedTypes.join(", ")}` };
    }

    if (file.size > maxSize) {
      return { valid: false, error: `File too large. Maximum: 200MB` };
    }

    return { valid: true };
  }

  validateAudioFile(file, isDemoAudio = false) {
    const allowedTypes = [".mp3", ".wav", ".m4a"];
    const maxSize = isDemoAudio ? 50 * 1024 * 1024 : 500 * 1024 * 1024;

    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!allowedTypes.includes(`.${ext}`)) {
      return { valid: false, error: `Invalid audio type. Allowed: ${allowedTypes.join(", ")}` };
    }

    if (file.size > maxSize) {
      const maxMB = maxSize / (1024 * 1024);
      return { valid: false, error: `File too large. Maximum: ${maxMB}MB` };
    }

    return { valid: true };
  }
}

export default new FileService();
